import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { scheduleIsolatedMeasure, setInvisibleEntities, setSelectedEntities } from "@/Context/ViewerContext/Reducer";
import { ViewerContext } from "@/Context/ViewerContext/ViewerContextProvider";
import { XktContext } from "@/Context/XktContext/XktContextProvider";
import useFlyTo from "@/Hooks/FlyTo/FlyTo";
import { CircleDot, Eye, EyeOff, Fullscreen, Maximize, PencilRuler } from "lucide-react";
import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface CanvasContextMenuProps {
    canvasId: string;
    isLongPressing: React.MutableRefObject<boolean>;
}

const CanvasContextMenu = ({ canvasId, isLongPressing }: CanvasContextMenuProps) => {
    const { t } = useTranslation();
    const viewerCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const { state: viewerState, dispatch } = useContext(ViewerContext);
    const { state } = useContext(XktContext);
    const { flyTo } = useFlyTo();
    const downPos = useRef({ x: 0, y: 0 });
    const touchStart = useRef<{ x: number; y: number; timer: ReturnType<typeof setTimeout> } | null>(null);

    const mouseDown = (event: MouseEvent) => {
        downPos.current = { x: event.clientX, y: event.clientY };
    };

    const onTouchStart = (event: TouchEvent) => {
        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        const x = touch.clientX;
        const y = touch.clientY;

        const timer = setTimeout(() => {
            if (!triggerRef.current) return;
            isLongPressing.current = true;
            triggerRef.current.dispatchEvent(
                new MouseEvent("contextmenu", {
                    bubbles: true,
                    clientX: x,
                    clientY: y,
                })
            );
            touchStart.current = null;
        }, 500);

        touchStart.current = { x, y, timer };
    };

    const onTouchMove = (event: TouchEvent) => {
        if (!touchStart.current) return;

        const touch = event.touches[0];
        const diff = Math.abs(touch.clientX - touchStart.current.x) + Math.abs(touch.clientY - touchStart.current.y);

        if (diff > 10) {
            clearTimeout(touchStart.current.timer);
            touchStart.current = null;
        }
    };

    const onTouchEnd = () => {
        if (!touchStart.current) return;
        clearTimeout(touchStart.current.timer);
        touchStart.current = null;
        // eslint-disable-next-line react-hooks/immutability
        isLongPressing.current = false;
    };

    const hide = () => {
        if (viewerState.selectedEntities.length === 0) return;

        const merged = [...viewerState.invisibleEntities, ...viewerState.selectedEntities].filter(
            (v, i, a) => a.indexOf(v) === i
        );
        dispatch(setInvisibleEntities(merged as string[]));
    };

    const flyToSelection = async () => {
        if (!state.viewer) return;
        if (viewerState.selectedEntities.length === 0) return;

        dispatch(setSelectedEntities([]));

        flyTo(viewerState.selectedEntities as string[]);
    };

    const showEverything = () => {
        if (!state.viewer) return;

        const defaultInvisibleItems = [];
        if (!viewerState.gridEnabled && state.viewer.metaScene.metaObjectsByType["IfcGrid"]) {
            defaultInvisibleItems.push(...Object.keys(state.viewer.metaScene.metaObjectsByType["IfcGrid"]));
        }

        if (!viewerState.spaceEnabled && state.viewer.metaScene.metaObjectsByType["IfcSpace"]) {
            defaultInvisibleItems.push(...Object.keys(state.viewer.metaScene.metaObjectsByType["IfcSpace"]));
        }

        dispatch(setSelectedEntities([]));
        dispatch(setInvisibleEntities(defaultInvisibleItems));
    };

    const isolate = () => {
        if (!state.viewer) return;
        const keys = Object.keys(state.viewer.scene.objects).filter((e) => !viewerState.selectedEntities.includes(e));
        dispatch(setSelectedEntities([]));
        dispatch(setInvisibleEntities(keys));
        postExec();
    };

    const zoomOut = () => {
        if (!state.viewer) return;
        state.viewer.camera.zoom(2);
        postExec();
    };

    const measure = () => {
        if (!state.viewer) return;
        dispatch(scheduleIsolatedMeasure(viewerState.invisibleEntities as string[]));
        isolate();
        postExec();
    };

    const postExec = () => {
        dispatch(setSelectedEntities([]));
    };

    const onContextMenu = (e: MouseEvent) => {
        e.preventDefault();

        if (!triggerRef.current) return;
        const currentPos = { x: e.clientX, y: e.clientY };
        const diff = Math.abs(currentPos.x - downPos.current.x + currentPos.y - downPos.current.y);

        if (diff < 20) {
            triggerRef.current.dispatchEvent(
                new MouseEvent("contextmenu", {
                    bubbles: true,
                    clientX: e.clientX,
                    clientY: e.clientY,
                })
            );
        }
    };

    useEffect(() => {
        viewerCanvasRef.current = document.getElementById(canvasId) as HTMLCanvasElement;
        viewerCanvasRef.current.addEventListener("contextmenu", onContextMenu);
        viewerCanvasRef.current.addEventListener("mousedown", mouseDown);
        viewerCanvasRef.current.addEventListener("touchstart", onTouchStart);
        viewerCanvasRef.current.addEventListener("touchmove", onTouchMove);
        viewerCanvasRef.current.addEventListener("touchend", onTouchEnd);

        return () => {
            viewerCanvasRef.current?.removeEventListener("contextmenu", onContextMenu);
            viewerCanvasRef.current?.removeEventListener("mousedown", mouseDown);
            viewerCanvasRef.current?.removeEventListener("touchstart", onTouchStart);
            viewerCanvasRef.current?.removeEventListener("touchmove", onTouchMove);
            viewerCanvasRef.current?.removeEventListener("touchend", onTouchEnd);
        };
    }, []);

    return (
        <ContextMenu>
            <ContextMenuTrigger ref={triggerRef} />
            <ContextMenuContent className="w-64">
                <ContextMenuItem inset>{t("cancel")}</ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem inset onClick={showEverything}>
                    <Eye className="h-4 w-4 mr-2" />
                    {t("showEverything")}
                </ContextMenuItem>

                {viewerState.selectedEntities.length > 0 && (
                    <>
                        <ContextMenuSeparator />
                        <ContextMenuItem inset onClick={zoomOut}>
                            <Maximize className="h-4 w-4 mr-2" />
                            {t("zoomOut")}
                        </ContextMenuItem>
                        <ContextMenuItem inset onClick={flyToSelection}>
                            <Fullscreen className="h-4 w-4 mr-2" />
                            {t("zoomToFit")}
                        </ContextMenuItem>
                        <ContextMenuSeparator />
                        <ContextMenuItem inset onClick={hide}>
                            <EyeOff className="h-4 w-4 mr-2" />
                            {t("hide")}
                        </ContextMenuItem>
                        <ContextMenuItem inset onClick={isolate}>
                            <CircleDot className="h-4 w-4 mr-2" />
                            {t("isolate")}
                        </ContextMenuItem>
                        {viewerState.openPlugin != "SectionPlanes" && (
                            <ContextMenuItem inset onClick={measure}>
                                <PencilRuler className="h-4 w-4 mr-2" />
                                {t("measure")}
                            </ContextMenuItem>
                        )}
                    </>
                )}
            </ContextMenuContent>
        </ContextMenu>
    );
};

export default CanvasContextMenu;
